import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../Footer';
import BackLink from '../BackLink';
import '../Blog.css';

function ReplyDiagram() {
  return (
    <svg viewBox="0 0 680 360" role="img" aria-labelledby="reply-d-title reply-d-desc">
      <title id="reply-d-title">How a chat reply gets drafted</title>
      <desc id="reply-d-desc">
        The keyboard opens in a chat and a button appears. You tap it. Panda reads the messages on screen and writes a draft into your text box. You read it and press Send yourself. Panda never sends the message.
      </desc>
      <defs>
        <marker id="reply-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" className="byok-ah" />
        </marker>
      </defs>

      <rect x="16" y="28" width="140" height="88" rx="8" className="byok-box" />
      <text x="86" y="62" textAnchor="middle" className="byok-ink" fontSize="15" fontWeight="600">Keyboard</text>
      <text x="86" y="84" textAnchor="middle" className="byok-mut" fontSize="13">opens in a chat</text>

      <line x1="156" y1="72" x2="188" y2="72" className="byok-ln" markerEnd="url(#reply-arrow)" />

      <rect x="192" y="28" width="140" height="88" rx="8" className="byok-box" />
      <text x="262" y="62" textAnchor="middle" className="byok-ink" fontSize="15" fontWeight="600">Button</text>
      <text x="262" y="84" textAnchor="middle" className="byok-mut" fontSize="13">shows on its own</text>

      <line x1="332" y1="72" x2="364" y2="72" className="byok-ln" markerEnd="url(#reply-arrow)" />

      <rect x="368" y="16" width="140" height="112" rx="8" className="byok-box-key" />
      <text x="438" y="52" textAnchor="middle" className="byok-ink" fontSize="15" fontWeight="600">You tap</text>
      <text x="438" y="76" textAnchor="middle" className="byok-mut" fontSize="13">Panda reads</text>
      <text x="438" y="96" textAnchor="middle" className="byok-mut" fontSize="13">this chat</text>

      <line x1="508" y1="72" x2="540" y2="72" className="byok-ln" markerEnd="url(#reply-arrow)" />

      <rect x="544" y="16" width="120" height="112" rx="8" className="byok-box-key" />
      <text x="604" y="58" textAnchor="middle" className="byok-ink" fontSize="15" fontWeight="600">Draft</text>
      <text x="604" y="82" textAnchor="middle" className="byok-mut" fontSize="13">in your box</text>

      <line x1="604" y1="128" x2="604" y2="168" className="byok-ln" markerEnd="url(#reply-arrow)" />

      <rect x="470" y="172" width="194" height="88" rx="8" className="byok-box" />
      <text x="567" y="206" textAnchor="middle" className="byok-ink" fontSize="15" fontWeight="600">You press Send</text>
      <text x="567" y="230" textAnchor="middle" className="byok-mut" fontSize="13">Panda never does</text>

      <rect x="16" y="220" width="420" height="112" rx="8" className="byok-box" />
      <text x="226" y="256" textAnchor="middle" className="byok-ink" fontSize="15" fontWeight="600">Before you tap, nothing is read</text>
      <text x="226" y="282" textAnchor="middle" className="byok-mut" fontSize="13">After you tap, only this chat on screen</text>
      <text x="226" y="304" textAnchor="middle" className="byok-mut" fontSize="13">Not quite right? Ask for another, or a shorter one</text>
    </svg>
  );
}

export default function ChatReplyPost() {
  return (
    <div className="story-page">
      <article className="story-inner">
        <BackLink to="/blog" className="story-back">Blog</BackLink>
        <h1>A reply, ready when the keyboard opens</h1>
        <p className="news-meta">
          <span>Product</span>
          <span>October 2026</span>
          <span>4 min read</span>
        </p>
        <div className="story-body">
          <p>
            In a chat, when your keyboard opens, Panda puts a small button above the text box: Draft reply with Panda. Tap it, and a reply lands in the box, written the way you write. You read it, change it if you want, and send it yourself.
          </p>
          <p>
            The button shows on its own. The message does not go out on its own. Panda never presses Send.
          </p>

          <h2>Where it shows up</h2>
          <p>
            WhatsApp, WhatsApp Business, Instagram, Telegram, Messenger, Signal, Google Messages, Slack, Discord, Microsoft Teams, Google Chat, X, and LinkedIn. It stays out of other apps, and it never sits on a password field.
          </p>

          <figure className="byok-fig">
            <div className="byok-fig-box">
              <ReplyDiagram />
            </div>
            <figcaption>
              The button appears with the keyboard. A draft is written only after you tap. You are the one who sends it.
            </figcaption>
          </figure>

          <h2>What Panda looks at</h2>
          <p>
            Nothing in the chat is read until you tap. The button itself does not send your messages anywhere.
          </p>
          <p>
            After you tap, Panda reads the last few messages on the screen, up to eight, and which ones are yours. If an app hides that text, Panda takes one picture of the message area only: not your notifications, not the contact photo, not the keyboard. On X, it always uses that picture, so keep the post you are replying to visible above the keyboard.
          </p>
          <p>
            That text, or that picture, goes to Panda so a reply can be written and put back in your box. Your first 100 drafts are free.
          </p>

          <h2>If it is not quite right</h2>
          <p>
            Two buttons stay above the box. One writes a different reply. The other changes the one you have: warmer, neutral, sympathetic, shorter, or longer. A change rewrites what is in the box now, so anything you already edited stays in the mix.
          </p>

          <h2>Turn it off</h2>
          <p>
            The × on the button hides it in that chat until you leave the app. To turn the feature off, open Panda’s Settings and switch off Reply suggestions when keyboard opens. The same switch turns it back on.
          </p>
          <p>
            More detail is on the <Link to="/why-chat-reply-helper">Chat Reply Helper</Link> page.
          </p>
        </div>
      </article>
      <Footer />
    </div>
  );
}
